import React from "react";

import PrimaryButton from "~/components/buttons/primaryButton";
import SecondaryButton from "~/components/buttons/secondaryButton";
import CalendarIcon from "~/components/icons/calendarIcon";
import { getApiErrorMessage } from "~/services/apiErrors";
import { validateScheduledDateAfterNow } from "~/services/customerInteractionService";

export type CreateTaskSubmission = {
  title: string;
  scheduledAtMs: number;
};

type CreateTaskModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onDeferAndNavigateDashboard?: () => void;
  onSubmit: (payload: CreateTaskSubmission) => void | Promise<void>;
  customerName?: string | null;
  callerNumber?: string | null;
  defaultTitle?: string;
  className?: string;
};

function cn(...classes: Array<string | undefined | false | null>) {
  return classes.filter(Boolean).join(" ");
}

function toDateTimeLocalValue(timestampMs: number) {
  const date = new Date(timestampMs);
  if (Number.isNaN(date.getTime())) return "";
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

export default function CreateTaskModal({
  isOpen,
  onClose,
  onDeferAndNavigateDashboard,
  onSubmit,
  customerName,
  callerNumber,
  defaultTitle,
  className,
}: CreateTaskModalProps) {
  const [title, setTitle] = React.useState("");
  const [scheduledAtIso, setScheduledAtIso] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) return;
    setTitle(defaultTitle ?? "");
    setScheduledAtIso(toDateTimeLocalValue(Date.now() + 86_400_000));
    setFormError(null);
    setSubmitError(null);
    setSubmitting(false);
  }, [defaultTitle, isOpen]);

  const handleSave = React.useCallback(async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setFormError("Task title is required.");
      return;
    }
    if (!scheduledAtIso) {
      setFormError("Scheduled date and time are required.");
      return;
    }

    const scheduledAtMs = new Date(scheduledAtIso).getTime();
    if (!validateScheduledDateAfterNow(scheduledAtMs)) {
      setFormError("Scheduled date must be in the future.");
      return;
    }

    setFormError(null);
    setSubmitError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        title: trimmed,
        scheduledAtMs,
      });
    } catch (error) {
      const parsed = getApiErrorMessage(
        error,
        "Unable to save follow-up task. Please try again."
      );
      setSubmitError(parsed.message);
    } finally {
      setSubmitting(false);
    }
  }, [onSubmit, scheduledAtIso, title]);

  if (!isOpen) return null;

  return (
    <div className={cn("fixed inset-0 z-70", className)}>
      <button
        type="button"
        aria-label="Close create task modal"
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />

      <div className="relative flex min-h-full items-center justify-center p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Create follow-up task"
          className={cn(
            "w-155 max-w-[calc(100vw-24px)]",
            "rounded-[18px] border border-[#2C313B]",
            "bg-[#0B111C] text-white",
            "shadow-[0_26px_90px_rgba(0,0,0,0.65)]"
          )}
        >
          <div className="px-8 py-8 md:px-10">
            <h2 className="text-[20px] font-semibold leading-7 text-white">
              Create Follow-up Task
            </h2>

            {customerName || callerNumber ? (
              <div className="mt-5 rounded-xl border border-[#2C313B] bg-[#101726] px-4 py-3 text-[13px] text-[#C4CBD7]">
                {customerName ? (
                  <div>
                    <span className="text-[#8F98A8]">Customer:</span>{" "}
                    {customerName}
                  </div>
                ) : null}
                {callerNumber ? (
                  <div className={customerName ? "mt-1" : undefined}>
                    <span className="text-[#8F98A8]">Caller Number:</span>{" "}
                    {callerNumber}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="mt-6">
              <label className="block text-[14px] font-normal leading-5 text-[#99A1AA]">
                Task
              </label>
              <input
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                  if (formError) setFormError(null);
                  if (submitError) setSubmitError(null);
                }}
                spellCheck={true}
                placeholder="Add title"
                className={cn(
                  "mt-2 h-11 w-full rounded-[10px] border border-[#2C313B]",
                  "bg-transparent px-4 text-[16px] text-white",
                  "placeholder:text-[#727C92]",
                  "focus:outline-none focus:border-[#2C313B]"
                )}
              />
            </div>

            <div className="mt-5">
              <label className="block text-[14px] font-normal leading-5 text-[#99A1AA]">
                Scheduled date and time
              </label>
              <div className="relative mt-2">
                <input
                  type="datetime-local"
                  value={scheduledAtIso}
                  onChange={(event) => {
                    setScheduledAtIso(event.target.value);
                    if (formError) setFormError(null);
                    if (submitError) setSubmitError(null);
                  }}
                  className={cn(
                    "h-11 w-full rounded-[10px] border border-[#2C313B]",
                    "bg-transparent px-4 pr-10 text-[16px] text-white",
                    "focus:outline-none focus:border-[#2C313B]"
                  )}
                />
                <CalendarIcon className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#727C92]" />
              </div>
            </div>

            {formError ? (
              <div className="mt-3 text-[12px] text-red-300">{formError}</div>
            ) : null}
            {submitError ? (
              <div className="mt-3 text-[12px] text-red-300">{submitError}</div>
            ) : null}

            <div className="mt-8 flex flex-wrap items-center justify-end gap-4">
              <SecondaryButton
                className="h-11 min-w-30 px-6 text-[16px] text-white"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </SecondaryButton>
              {onDeferAndNavigateDashboard ? (
                <SecondaryButton
                  data-testid="create-task-return-dashboard"
                  className="h-11 min-w-45 px-6 text-[16px] text-white"
                  onClick={onDeferAndNavigateDashboard}
                  disabled={submitting}
                >
                  Return to Dashboard
                </SecondaryButton>
              ) : null}
              <PrimaryButton
                className="h-11 min-w-37.5 px-6 text-[16px]"
                onClick={() => {
                  void handleSave();
                }}
                loading={submitting}
                disabled={submitting}
              >
                Save Task
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
